import React, { memo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  Box,
  Skeleton,
  TablePagination
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, CalendarMonth as CalendarIcon } from '@mui/icons-material';
import type { MonthlyShiftSlotSummary } from '../../../../domain/monthlyShiftSlot';
import { formatYearMonthToSlash, formatRateAsPercent } from '../../../formatters';
import StatusChip from './StatusChip';

/**
 * MonthlyShiftSlotTable のProps型
 *
 * @property {MonthlyShiftSlotSummary[]} data - 表示するサマリーデータ配列
 * @property {boolean} isLoading - ローディング状態
 * @property {object|null} meta - ページネーションメタ情報
 * @property {function} onPageChange - ページ変更コールバック
 * @property {function} onEdit - 編集ボタンクリックコールバック
 * @property {function} onEditShiftSlot - シフト枠編集ボタンクリックコールバック
 * @property {function} onDelete - 削除ボタンクリックコールバック
 */
interface Props {
  data: MonthlyShiftSlotSummary[];
  isLoading: boolean;
  meta: {
    currentPage: number;
    lastPage: number;
    perPage: number;
    total: number;
  } | null;
  onPageChange: (page: number) => void;
  onEdit: (year: number, month: number) => void;
  onEditShiftSlot: (year: number, month: number) => void;
  onDelete: (summary: MonthlyShiftSlotSummary) => void;
}

/**
 * 月次シフト枠一覧テーブルコンポーネント
 *
 * @description
 * 月次シフト枠サマリーをテーブル形式で表示する。
 * ローディング状態ではスケルトン、データ空の場合はメッセージを表示。
 * ページネーション、編集・シフト枠編集・削除アクションを提供。
 *
 * @param {Props} props - コンポーネントProps
 * @returns {React.ReactElement} テーブルコンポーネント
 */
const MonthlyShiftSlotTable: React.FC<Props> = memo(({
  data,
  isLoading,
  meta,
  onPageChange,
  onEdit,
  onEditShiftSlot,
  onDelete
}) => {
  const handleChangePage = (_: unknown, newPage: number) => {
    onPageChange(newPage + 1);
  };

  // ローディング時のスケルトン表示
  if (isLoading && data.length === 0) {
    return (
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>年月</TableCell>
              <TableCell>ステータス</TableCell>
              <TableCell>提出率</TableCell>
              <TableCell>未確定枠</TableCell>
              <TableCell>シフトカレンダー</TableCell>
              <TableCell>編集</TableCell>
              <TableCell>削除</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {[...Array(5)].map((_, index) => (
              <TableRow key={index}>
                <TableCell><Skeleton width={80} /></TableCell>
                <TableCell><Skeleton width={60} /></TableCell>
                <TableCell><Skeleton width={40} /></TableCell>
                <TableCell><Skeleton width={40} /></TableCell>
                <TableCell><Skeleton width={80} /></TableCell>
                <TableCell><Skeleton width={40} /></TableCell>
                <TableCell><Skeleton width={40} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  // データなし
  if (!isLoading && data.length === 0) {
    const now = new Date();
    const currentYearMonth = formatYearMonthToSlash(now.getFullYear(), now.getMonth() + 1);

    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">
          {currentYearMonth}の月次シフト枠を設定しておりません。
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width={120}>年月</TableCell>
              <TableCell width={100}>ステータス</TableCell>
              <TableCell width={100}>提出率</TableCell>
              <TableCell width={100}>未確定枠</TableCell>
              <TableCell width={120}>シフトカレンダー</TableCell>
              <TableCell width={80}>編集</TableCell>
              <TableCell width={80}>削除</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>
                  <Typography variant="body2">
                    {formatYearMonthToSlash(row.year, row.month)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <StatusChip status={row.status} />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {formatRateAsPercent(row.submissionRate)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {row.unconfirmedSlots}
                  </Typography>
                </TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    onClick={() => onEditShiftSlot(row.year, row.month)}
                    color="primary"
                  >
                    <CalendarIcon />
                  </IconButton>
                </TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    onClick={() => onEdit(row.year, row.month)}
                    color="primary"
                  >
                    <EditIcon />
                  </IconButton>
                </TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    onClick={() => onDelete(row)}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ページネーション */}
      {meta && (
        <TablePagination
          component="div"
          count={meta.total}
          page={meta.currentPage - 1}
          rowsPerPage={meta.perPage}
          onPageChange={handleChangePage}
          rowsPerPageOptions={[50]}
          labelRowsPerPage="表示件数:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} / ${count}件`
          }
        />
      )}
    </Box>
  );
});

MonthlyShiftSlotTable.displayName = 'MonthlyShiftSlotTable';

export default MonthlyShiftSlotTable;
